/** Baseline FHR variability amplitude */
export enum FHRVariability {
  /** Undetectable amplitude */
  ABSENT = 'absent',
  /** Amplitude > 0 but ≤ 5 bpm */
  MINIMAL = 'minimal',
  /** Amplitude 6–25 bpm (normal) */
  MODERATE = 'moderate',
  /** Amplitude > 25 bpm */
  MARKED = 'marked',
}

/** Type of fetal heart rate deceleration */
export enum FHRDeceleration {
  NONE = 'none',
  /** Uniform, gradual decrease associated with uterine contractions */
  EARLY = 'early',
  /** Abrupt decrease unrelated to contractions */
  VARIABLE = 'variable',
  /** Gradual decrease after the peak of a contraction */
  LATE = 'late',
  /** Decrease ≥ 15 bpm lasting > 2 minutes but < 10 minutes */
  PROLONGED = 'prolonged',
}

/** NICHD FHR tracing category */
export enum FHRCategory {
  /** Normal — strongly predictive of normal fetal acid-base status */
  I = 'I',
  /** Indeterminate — insufficient evidence to classify */
  II = 'II',
  /** Abnormal — predictive of abnormal fetal acid-base status */
  III = 'III',
}

/** Method used to assess fetal heart rate */
export enum FHRMethod {
  DOPPLER = 'doppler',
  FETOSCOPE = 'fetoscope',
  INTERNAL_SCALP_ELECTRODE = 'internal-scalp-electrode',
}

export enum FHRAction {
  /** Position change */
  POSITION_CHANGE = 'position-change',
  /** Oxygen */
  OXYGEN = 'oxygen',
  /** Fluid bolus */
  FLUID_BOLUS = 'fluid-bolus',
  /** IV access */
  IV_ACCESS = 'iv-access',
  /** Notify provider */
  NOTIFY_PROVIDER = 'notify-provider',
  /** Amnioinfusion */
  AMNIOINFUSION = 'amnioinfusion',
}

export interface FetalHeartRateExam {
  /** Average baseline fetal heart rate in bpm (typically 110–160) */
  baselineBpm: number
  /** Beat-to-beat variability of the FHR */
  variability: FHRVariability
  /** Whether accelerations ≥ 15 bpm for ≥ 15 seconds are present */
  accelerations: boolean
  /** Type of deceleration pattern observed */
  decelerations: FHRDeceleration
  /** Actions taken to improve FHR */
  actions: FHRAction[]
  /** NICHD tracing category */
  category: FHRCategory
  /** Method used to monitor fetal heart rate */
  method: FHRMethod
}
